import { z } from 'zod'
import type { CrudField, CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'

type Translate = (key: string, fallback: string, params?: Record<string, string | number>) => string

export type CargoFormValues = {
  cargoType?: string
  referenceNumber?: string
  description?: string
  containerNumber?: string
  containerSizeType?: string
  sealNumber?: string
  gs1Sscc?: string
  palletCount?: number | string
  palletType?: string
  palletStackable?: boolean
  bulkMaterial?: string
  bulkUnit?: string
  bulkQuantity?: number | string
  weightKg?: number | string
  volumeM3?: number | string
  packageCount?: number | string
  isDangerous?: boolean
  dangerousGoodsUnNumber?: string
  dangerousGoodsClass?: string
  dangerousGoodsPackingGroup?: string
  dangerousGoodsNotes?: string
  requiresTempControl?: boolean
  tempMinC?: number | string
  tempMaxC?: number | string
  currentMode?: string
  currentLocationLabel?: string
  currentLocationLatitude?: number | string
  currentLocationLongitude?: number | string
  currentLocationSource?: string
  salesShipmentId?: string
  executionReferenceKind?: string
  executionReferenceId?: string
  assignmentRelationship?: string
  assignmentNotes?: string
}

type CargoFormDetail = {
  cargoType?: string | null
  referenceNumber?: string | null
  description?: string | null
  containerNumber?: string | null
  containerSizeType?: string | null
  sealNumber?: string | null
  gs1Sscc?: string | null
  palletCount?: number | null
  palletType?: string | null
  palletStackable?: boolean | null
  bulkMaterial?: string | null
  bulkUnit?: string | null
  bulkQuantity?: number | null
  weightKg?: number | null
  volumeM3?: number | null
  packageCount?: number | null
  isDangerous?: boolean | null
  dangerousGoodsUnNumber?: string | null
  dangerousGoodsClass?: string | null
  dangerousGoodsPackingGroup?: string | null
  dangerousGoodsNotes?: string | null
  requiresTempControl?: boolean | null
  tempMinC?: number | null
  tempMaxC?: number | null
  currentMode?: string | null
  currentLocation?: {
    label?: string | null
    latitude?: number | null
    longitude?: number | null
    source?: string | null
  } | null
  assignments?: Array<{
    salesShipmentId?: string | null
    executionReferenceKind?: string | null
    executionReferenceId?: string | null
    relationship?: string | null
    notes?: string | null
  }> | null
}

const toNullableText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.length) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function buildCargoFormSchema(translate: Translate) {
  return z.object({
    cargoType: z.string().optional(),
    referenceNumber: z.string().optional(),
    description: z.string().optional(),
    containerNumber: z.string().optional(),
    containerSizeType: z.string().optional(),
    sealNumber: z.string().optional(),
    gs1Sscc: z.string().optional(),
    palletCount: z.union([z.string(), z.number()]).optional(),
    palletType: z.string().optional(),
    palletStackable: z.boolean().optional(),
    bulkMaterial: z.string().optional(),
    bulkUnit: z.string().optional(),
    bulkQuantity: z.union([z.string(), z.number()]).optional(),
    weightKg: z.union([z.string(), z.number()]).optional(),
    volumeM3: z.union([z.string(), z.number()]).optional(),
    packageCount: z.union([z.string(), z.number()]).optional(),
    isDangerous: z.boolean().optional(),
    dangerousGoodsUnNumber: z.string().optional(),
    dangerousGoodsClass: z.string().optional(),
    dangerousGoodsPackingGroup: z.string().optional(),
    dangerousGoodsNotes: z.string().optional(),
    requiresTempControl: z.boolean().optional(),
    tempMinC: z.union([z.string(), z.number()]).optional(),
    tempMaxC: z.union([z.string(), z.number()]).optional(),
    currentMode: z.string().optional(),
    currentLocationLabel: z.string().optional(),
    currentLocationLatitude: z.union([z.string(), z.number()]).optional(),
    currentLocationLongitude: z.union([z.string(), z.number()]).optional(),
    currentLocationSource: z.string().optional(),
    salesShipmentId: z.string().optional(),
    executionReferenceKind: z.string().optional(),
    executionReferenceId: z.string().optional(),
    assignmentRelationship: z.string().optional(),
    assignmentNotes: z.string().optional(),
  }).superRefine((values, ctx) => {
    const referenceNumber = toNullableText(values.referenceNumber)
    if (!referenceNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translate('cargo.form.errors.referenceNumber', 'Reference number is required.'),
        path: ['referenceNumber'],
      })
    }
  })
}

export function buildCargoFields(t: Translate): CrudField[] {
  const cargoTypeOptions = [
    { value: 'general', label: t('cargo.types.general', 'General') },
    { value: 'container', label: t('cargo.types.container', 'Container') },
    { value: 'pallet', label: t('cargo.types.pallet', 'Pallet') },
    { value: 'bulk', label: t('cargo.types.bulk', 'Bulk') },
  ]
  const currentModeOptions = [
    { value: 'road', label: t('cargo.modes.road', 'Road') },
    { value: 'sea', label: t('cargo.modes.sea', 'Sea') },
    { value: 'rail', label: t('cargo.modes.rail', 'Rail') },
    { value: 'air', label: t('cargo.modes.air', 'Air') },
    { value: 'warehouse', label: t('cargo.modes.warehouse', 'Warehouse') },
    { value: 'terminal', label: t('cargo.modes.terminal', 'Terminal') },
  ]

  return [
    {
      id: 'cargoType',
      label: t('cargo.form.fields.cargoType', 'Cargo type'),
      type: 'select',
      options: cargoTypeOptions,
      required: true,
    },
    {
      id: 'referenceNumber',
      label: t('cargo.form.fields.referenceNumber', 'Reference number'),
      type: 'text',
      required: true,
    },
    {
      id: 'description',
      label: t('cargo.form.fields.description', 'Description'),
      type: 'textarea',
    },
    {
      id: 'containerNumber',
      label: t('cargo.form.fields.containerNumber', 'Container number'),
      type: 'text',
    },
    {
      id: 'containerSizeType',
      label: t('cargo.form.fields.containerSizeType', 'ISO size/type'),
      type: 'text',
    },
    {
      id: 'sealNumber',
      label: t('cargo.form.fields.sealNumber', 'Seal number'),
      type: 'text',
    },
    {
      id: 'gs1Sscc',
      label: t('cargo.form.fields.gs1Sscc', 'GS1 SSCC'),
      type: 'text',
    },
    {
      id: 'palletCount',
      label: t('cargo.form.fields.palletCount', 'Pallet count'),
      type: 'number',
    },
    {
      id: 'palletType',
      label: t('cargo.form.fields.palletType', 'Pallet type'),
      type: 'text',
    },
    {
      id: 'palletStackable',
      label: t('cargo.form.fields.palletStackable', 'Stackable pallets'),
      type: 'checkbox',
    },
    {
      id: 'bulkMaterial',
      label: t('cargo.form.fields.bulkMaterial', 'Bulk material'),
      type: 'text',
    },
    {
      id: 'bulkUnit',
      label: t('cargo.form.fields.bulkUnit', 'Bulk unit'),
      type: 'text',
    },
    {
      id: 'bulkQuantity',
      label: t('cargo.form.fields.bulkQuantity', 'Bulk quantity'),
      type: 'number',
    },
    {
      id: 'weightKg',
      label: t('cargo.form.fields.weightKg', 'Weight (kg)'),
      type: 'number',
    },
    {
      id: 'volumeM3',
      label: t('cargo.form.fields.volumeM3', 'Volume (m3)'),
      type: 'number',
    },
    {
      id: 'packageCount',
      label: t('cargo.form.fields.packageCount', 'Package count'),
      type: 'number',
    },
    {
      id: 'isDangerous',
      label: t('cargo.form.fields.isDangerous', 'Dangerous goods'),
      type: 'checkbox',
    },
    {
      id: 'dangerousGoodsUnNumber',
      label: t('cargo.form.fields.dangerousGoodsUnNumber', 'UN number'),
      type: 'text',
    },
    {
      id: 'dangerousGoodsClass',
      label: t('cargo.form.fields.dangerousGoodsClass', 'DG class'),
      type: 'text',
    },
    {
      id: 'dangerousGoodsPackingGroup',
      label: t('cargo.form.fields.dangerousGoodsPackingGroup', 'Packing group'),
      type: 'text',
    },
    {
      id: 'dangerousGoodsNotes',
      label: t('cargo.form.fields.dangerousGoodsNotes', 'Dangerous goods notes'),
      type: 'textarea',
    },
    {
      id: 'requiresTempControl',
      label: t('cargo.form.fields.requiresTempControl', 'Temperature controlled'),
      type: 'checkbox',
    },
    {
      id: 'tempMinC',
      label: t('cargo.form.fields.tempMinC', 'Min temp (C)'),
      type: 'number',
    },
    {
      id: 'tempMaxC',
      label: t('cargo.form.fields.tempMaxC', 'Max temp (C)'),
      type: 'number',
    },
    {
      id: 'currentMode',
      label: t('cargo.form.fields.currentMode', 'Current mode'),
      type: 'select',
      options: currentModeOptions,
    },
    {
      id: 'currentLocationLabel',
      label: t('cargo.form.fields.currentLocationLabel', 'Location label'),
      type: 'text',
    },
    {
      id: 'currentLocationLatitude',
      label: t('cargo.form.fields.currentLocationLatitude', 'Latitude'),
      type: 'number',
    },
    {
      id: 'currentLocationLongitude',
      label: t('cargo.form.fields.currentLocationLongitude', 'Longitude'),
      type: 'number',
    },
    {
      id: 'currentLocationSource',
      label: t('cargo.form.fields.currentLocationSource', 'Location source'),
      type: 'text',
    },
    {
      id: 'salesShipmentId',
      label: t('cargo.form.fields.salesShipmentId', 'Sales shipment ID'),
      type: 'text',
    },
    {
      id: 'executionReferenceKind',
      label: t('cargo.form.fields.executionReferenceKind', 'Execution reference kind'),
      type: 'text',
    },
    {
      id: 'executionReferenceId',
      label: t('cargo.form.fields.executionReferenceId', 'Execution reference ID'),
      type: 'text',
    },
    {
      id: 'assignmentRelationship',
      label: t('cargo.form.fields.assignmentRelationship', 'Assignment relationship'),
      type: 'text',
    },
    {
      id: 'assignmentNotes',
      label: t('cargo.form.fields.assignmentNotes', 'Assignment notes'),
      type: 'textarea',
    },
  ]
}

export function buildCargoGroups(t: Translate): CrudFormGroup[] {
  return [
    {
      id: 'summary',
      title: t('cargo.form.groups.summary', 'Summary'),
      fields: ['cargoType', 'referenceNumber', 'description', 'weightKg', 'volumeM3', 'packageCount'],
    },
    {
      id: 'container',
      title: t('cargo.form.groups.container', 'Container'),
      fields: ['containerNumber', 'containerSizeType', 'sealNumber', 'gs1Sscc'],
    },
    {
      id: 'pallet',
      title: t('cargo.form.groups.pallet', 'Pallet'),
      fields: ['palletCount', 'palletType', 'palletStackable'],
    },
    {
      id: 'bulk',
      title: t('cargo.form.groups.bulk', 'Bulk'),
      fields: ['bulkMaterial', 'bulkUnit', 'bulkQuantity'],
    },
    {
      id: 'compliance',
      title: t('cargo.form.groups.compliance', 'Compliance'),
      fields: [
        'isDangerous',
        'dangerousGoodsUnNumber',
        'dangerousGoodsClass',
        'dangerousGoodsPackingGroup',
        'dangerousGoodsNotes',
      ],
    },
    {
      id: 'temperature',
      title: t('cargo.form.groups.temperature', 'Temperature'),
      fields: ['requiresTempControl', 'tempMinC', 'tempMaxC'],
    },
    {
      id: 'operations',
      title: t('cargo.form.groups.operations', 'Live operations'),
      fields: [
        'currentMode',
        'currentLocationLabel',
        'currentLocationLatitude',
        'currentLocationLongitude',
        'currentLocationSource',
      ],
    },
    {
      id: 'links',
      title: t('cargo.form.groups.links', 'Links'),
      fields: [
        'salesShipmentId',
        'executionReferenceKind',
        'executionReferenceId',
        'assignmentRelationship',
        'assignmentNotes',
      ],
    },
  ]
}

export function buildInitialCargoValues(preset?: { salesShipmentId?: string | null }): Partial<CargoFormValues> {
  return {
    cargoType: 'general',
    isDangerous: false,
    requiresTempControl: false,
    assignmentRelationship: 'primary',
    salesShipmentId: preset?.salesShipmentId ?? '',
  }
}

export function buildCargoFormValues(detail: CargoFormDetail | null | undefined): Partial<CargoFormValues> {
  const primaryAssignment = detail?.assignments?.[0]

  return {
    cargoType: detail?.cargoType ?? 'general',
    referenceNumber: detail?.referenceNumber ?? '',
    description: detail?.description ?? '',
    containerNumber: detail?.containerNumber ?? '',
    containerSizeType: detail?.containerSizeType ?? '',
    sealNumber: detail?.sealNumber ?? '',
    gs1Sscc: detail?.gs1Sscc ?? '',
    palletCount: detail?.palletCount ?? '',
    palletType: detail?.palletType ?? '',
    palletStackable: detail?.palletStackable ?? false,
    bulkMaterial: detail?.bulkMaterial ?? '',
    bulkUnit: detail?.bulkUnit ?? '',
    bulkQuantity: detail?.bulkQuantity ?? '',
    weightKg: detail?.weightKg ?? '',
    volumeM3: detail?.volumeM3 ?? '',
    packageCount: detail?.packageCount ?? '',
    isDangerous: detail?.isDangerous ?? false,
    dangerousGoodsUnNumber: detail?.dangerousGoodsUnNumber ?? '',
    dangerousGoodsClass: detail?.dangerousGoodsClass ?? '',
    dangerousGoodsPackingGroup: detail?.dangerousGoodsPackingGroup ?? '',
    dangerousGoodsNotes: detail?.dangerousGoodsNotes ?? '',
    requiresTempControl: detail?.requiresTempControl ?? false,
    tempMinC: detail?.tempMinC ?? '',
    tempMaxC: detail?.tempMaxC ?? '',
    currentMode: detail?.currentMode ?? '',
    currentLocationLabel: detail?.currentLocation?.label ?? '',
    currentLocationLatitude: detail?.currentLocation?.latitude ?? '',
    currentLocationLongitude: detail?.currentLocation?.longitude ?? '',
    currentLocationSource: detail?.currentLocation?.source ?? '',
    salesShipmentId: primaryAssignment?.salesShipmentId ?? '',
    executionReferenceKind: primaryAssignment?.executionReferenceKind ?? '',
    executionReferenceId: primaryAssignment?.executionReferenceId ?? '',
    assignmentRelationship: primaryAssignment?.relationship ?? 'primary',
    assignmentNotes: primaryAssignment?.notes ?? '',
  }
}

export function buildCargoPayload(values: CargoFormValues) {
  const locationLabel = toNullableText(values.currentLocationLabel)
  const locationLatitude = toNullableNumber(values.currentLocationLatitude)
  const locationLongitude = toNullableNumber(values.currentLocationLongitude)
  const locationSource = toNullableText(values.currentLocationSource)
  const currentLocation =
    locationLabel || locationLatitude !== null || locationLongitude !== null || locationSource
      ? {
          label: locationLabel,
          latitude: locationLatitude,
          longitude: locationLongitude,
          source: locationSource,
        }
      : null

  const salesShipmentId = toNullableText(values.salesShipmentId)
  const executionReferenceKind = toNullableText(values.executionReferenceKind)
  const executionReferenceId = toNullableText(values.executionReferenceId)
  const assignments =
    salesShipmentId || (executionReferenceKind && executionReferenceId)
      ? [
          {
            salesShipmentId,
            executionReferenceKind,
            executionReferenceId,
            relationship: toNullableText(values.assignmentRelationship) ?? 'primary',
            notes: toNullableText(values.assignmentNotes),
          },
        ]
      : []

  const rawCargoType = toNullableText(values.cargoType)
  const rawCurrentMode = toNullableText(values.currentMode)

  return {
    cargoType: rawCargoType ? rawCargoType.toLowerCase() : 'general',
    referenceNumber: toNullableText(values.referenceNumber),
    description: toNullableText(values.description),
    containerNumber: toNullableText(values.containerNumber),
    containerSizeType: toNullableText(values.containerSizeType),
    sealNumber: toNullableText(values.sealNumber),
    gs1Sscc: toNullableText(values.gs1Sscc),
    palletCount: toNullableNumber(values.palletCount),
    palletType: toNullableText(values.palletType),
    palletStackable: values.palletStackable ?? undefined,
    bulkMaterial: toNullableText(values.bulkMaterial),
    bulkUnit: toNullableText(values.bulkUnit),
    bulkQuantity: toNullableNumber(values.bulkQuantity),
    weightKg: toNullableNumber(values.weightKg),
    volumeM3: toNullableNumber(values.volumeM3),
    packageCount: toNullableNumber(values.packageCount),
    isDangerous: values.isDangerous ?? false,
    dangerousGoodsUnNumber: toNullableText(values.dangerousGoodsUnNumber),
    dangerousGoodsClass: toNullableText(values.dangerousGoodsClass),
    dangerousGoodsPackingGroup: toNullableText(values.dangerousGoodsPackingGroup),
    dangerousGoodsNotes: toNullableText(values.dangerousGoodsNotes),
    requiresTempControl: values.requiresTempControl ?? false,
    tempMinC: toNullableNumber(values.tempMinC),
    tempMaxC: toNullableNumber(values.tempMaxC),
    currentMode: rawCurrentMode ? rawCurrentMode.toLowerCase() : null,
    currentLocation,
    assignments,
  }
}
